import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Collapse } from 'react-bootstrap';
// import Loader from './Loader';
import type { EntityDetailsInterface } from '../types/entityDetails';

type EntityDetailsType = {
  entity_id?: string | null | undefined;
  entity_name?: string | null;
  entity_government_type?: string | null;
  entity_government_class?: string | null;
  entity_name_snakecase?: string | null;
  sf_name?: string | null;
  sf_id?: string | null;
  tax_commission_id?: string | null;
  ein?: string | null;
  finet_vendor_ids?: string | null;
  transparency_id?: string | null;

  sf_reports_to_transparent_utah?: boolean | null;
  sf_reports_to_privacy?: boolean | null;
  sf_reports_to_local_gov?: boolean | null;
  sf_reports_to_treasurer?: boolean | null;

  public_infrastructure_district?: boolean | null;
  infrastructure_financing_district?: boolean | null;
  special_project_entity?: boolean | null;
  governmental_non_profits?: boolean | null;
  fs_reports_with_parent?: boolean | null;
  fee_assessing_entity?: boolean | null;
  component_unit_type?: boolean | null;
  tax_holding_county?: boolean | null;

  created_in_salesforce_date?: string | null;
  creation_date?: string | null;

  website?: string | null;
  entity_description?: string | null;
  entity_purpose?: string | null;

  form_of_government?: string | null;
  number_of_governing_body_seats?: string | null;
  external_auditors?: string | null;

  billing_address?: string | null;
  billing_street?: string | null;
  billing_city?: string | null;
  billing_country?: string | null;
  billing_postal_code?: string | null;
  billing_state?: string | null;

  shipping_address?: string | null;
  shipping_street?: string | null;
  shipping_city?: string | null;
  shipping_postal_code?: string | null;
  shipping_state?: string | null;
  shipping_country?: string | null;

  fiscal_year_begins?: string | null;
  fiscal_year_end?: string | null;
  fiscal_year_change_effective_date?: string | null;
  fiscal_year_has_changed?: string | null;
  transparency_report_expense_revenue_start_date?: string | null;
  transparency_report_expense_revenue_end_date?: string | null;
  transparency_report_wage_start_date?: string | null;
  transparency_report_wage_end_date?: string | null;

  entity_status?: string | null;
  hold_funds?: boolean | null;
  hold_deposit_and_investment?: boolean | null;
  suspend_funds?: boolean | null;
  suspend_funds_deposit_and_investment?: boolean | null;
  freeze_funds?: boolean | null;
  freeze_funds_deposit_and_investment?: boolean | null;
  transparency_delinquency?: boolean | null;
  transparency_hold?: boolean | null;
  transparency_suspend?: boolean | null;
};

interface EntityPopulation {
  census_population_long_id: string;
  entity_id: string;
  entity_name: string;
  census_year: number;
  census_population: number;
  census_summary_level: string;
  census_state_fips_code: string;
  census_county_fips_code: string;
  census_place_fips_code: string;
  census_area_name: string;
}

interface EntityDetailsProps {
  loading: boolean;
  entityDetailsData: EntityDetailsInterface[];
  entityPopulationData?: EntityPopulation[];
}

// Null-safe display helper — catches null, undefined, empty strings, and literal "null"
const safeDisplay = (val: any): string => {
  if (val === null || val === undefined) return '—';
  const s = String(val).trim();
  if (s === '' || s.toLowerCase() === 'null') return '—';
  return s;
};

const EntityDetailsOverview: React.FC<EntityDetailsProps> = ({
  loading,
  entityDetailsData,
  entityPopulationData,
}) => {
  const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);
  // const [entityDetailsDataClean, setEntityDetailsDataClean] = useState<EntityDetailsInterface[]>([]); // Deleted for now - might not be necessary

  const formatDate = (iso?: string | null) => {
    if (iso === 'Current' || iso === 'Missing') return iso;
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso); // Added to check date validity
    d.setDate(d.getDate() + 1);
    // return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString('en-US');
    return d.toLocaleDateString('en-US');
  };

  // NOTE: Keeping the helper (even if unused) for future use
  const statusClass = (status?: string | null) => {
    const s = (status ?? '').toLowerCase().trim().replace(/\s+/g, '-');
    return s ? `tu-status ${s}` : 'tu-status';
  };

  // Updated to use tu-* pills instead of Tailwind classes
  const formatBoolean = (val: boolean | null | undefined) => {
    if (val === true) return <span className="tu-bool tu-bool--yes">Yes</span>;
    if (val === false) return <span className="tu-bool tu-bool--no">No</span>;
    return <span className="tu-bool tu-bool--na">—</span>;
  };

  // Added to strip year from fiscal year begins and fiscal year ends
  const stripFiscalYear = (formattedDate?: string | null) => {
    if (!formattedDate) return formattedDate;

    const firstSlashIndex = formattedDate.indexOf('/');
    const secondSlashIndex = formattedDate.indexOf('/', firstSlashIndex + 1);

    return secondSlashIndex !== -1 
      ? formattedDate.substring(0, secondSlashIndex) 
      : formattedDate;
  };

  // --- NEW: helper to render Office Hours nicely (if present in description) ---
  const renderDescription = (text?: string | null) => {
    if (!text) return <span className="tu-empty">N/A</span>;

    const raw = String(text);

    // split by newlines (best), or fallback to splitting on "Office hours" if it's inline
    const lines = raw.split(/\r?\n/).map(l => l.trim()).filter(Boolean);

    // find an "office hours" line (case-insensitive)
    const hoursLine =
      lines.find(l => l.toLowerCase().includes('office hours')) ||
      (raw.toLowerCase().includes('office hours') ? raw : null);

    // If found a specific line, remove it from body lines
    const bodyLines = hoursLine
      ? lines.filter(l => l !== hoursLine && !l.toLowerCase().includes('office hours'))
      : lines;

    const hoursText = hoursLine
      ? hoursLine.replace(/office hours\s*[-:]\s*/i, '').trim()
      : '';

    return (
      <div className="tu-textBlock">
        {bodyLines.length > 0 && <div>{bodyLines.join('\n')}</div>}

        {hoursLine && (
          <div className="tu-hours">
            <i className="far fa-clock" aria-hidden="true" />
            <span className="tu-hours__label">Office hours</span>
            <span className="tu-hours__value">{hoursText || '—'}</span>
          </div>
        )}
      </div>
    );
  };

  // Reusable UI components
  // --- UPDATED: SectionCard now supports an icon ---
  const SectionCard = ({
    title,
    icon,
    children,
  }: {
    title: string;
    icon?: string;
    children: React.ReactNode;
  }) => (
    <section className="tu-card">
      <header className="tu-card__header">
        <div className="tu-cardHeaderRow">
          {icon && (
            <span className="tu-cardIcon" aria-hidden="true">
              <i className={icon} />
            </span>
          )}
          <h3 className="tu-card__title">{title}</h3>
        </div>
      </header>
      <div className="tu-card__body">
        <dl className="tu-dl">{children}</dl>
      </div>
    </section>
  );

  const DetailItem = ({
    label,
    value,
    fullWidth = false,
  }: {
    label: string;
    value: React.ReactNode;
    fullWidth?: boolean;
  }) => (
    <div className={`tu-detail ${fullWidth ? 'tu-detail--full' : ''}`}>
      <dt className="tu-detail__label">{label}</dt>
      <dd className="tu-detail__value">{value || <span className="tu-empty">N/A</span>}</dd>
    </div>
  );

  // Keeping the StatusBadge helper (even if we don't render it yet)
  const StatusBadge = ({ status }: { status?: string | null }) => {
    if (!status) return null;

    const isDanger = ['Inactive', 'Suspended', 'Delinquent'].some((s) => status.includes(s));
    const isWarning = ['Pending', 'Review'].some((s) => status.includes(s));

    let colorClass = 'tu-status';
    if (isDanger) colorClass = 'tu-status missing';
    else if (isWarning) colorClass = 'tu-status pending';
    else colorClass = 'tu-status approved';

    return <span className={colorClass}>{status}</span>;
  };

  const formatAddress = (
    street?: string | null,
    city?: string | null,
    state?: string | null,
    zip?: string | null,
    country?: string | null
  ) => {
    // Added to get rid of null as country name
    if (state === 'UT' || state === 'Utah') {
      country = 'United States';
    }
    const parts = [street, city, state, zip, country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  // const entity: EntityDetailsInterface = entityDetailsDataClean?.[0] ?? ({} as EntityDetailsInterface);
  const entity: EntityDetailsInterface = entityDetailsData?.[0] ?? ({} as EntityDetailsInterface); // Updated implementation

 // Years since entity creation
  const entityAge = useMemo(() => {
    if (!entity.creation_date) return null;
    const d = new Date(entity.creation_date);
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    const years = now.getFullYear() - d.getFullYear();
    const hadBirthday =
      now.getMonth() > d.getMonth() ||
      (now.getMonth() === d.getMonth() && now.getDate() >= d.getDate());
    return hadBirthday ? years : years - 1;
  }, [entity.creation_date]);

  // Reporting compliance score
  const reportingCompliance = useMemo(() => {
    const obligations = [
      entity.sf_reports_to_local_gov,
      entity.sf_reports_to_treasurer,
      entity.sf_reports_to_transparent_utah,
    ];
    const active = obligations.filter((v) => v === true).length;
    const total = obligations.filter((v) => v === true || v === false).length;
    return { active, total };
  }, [entity.sf_reports_to_local_gov, entity.sf_reports_to_treasurer, entity.sf_reports_to_transparent_utah]);

  // Classification flags count
  const classificationFlags = useMemo(() => {
    const flags = [
      entity.public_infrastructure_district,
      entity.infrastructure_financing_district,
      entity.governmental_non_profits,
      entity.fee_assessing_entity,
      entity.fs_reports_with_parent,
    ];
    return flags.filter((v) => v === true).length;
  }, [
    entity.public_infrastructure_district,
    entity.infrastructure_financing_district,
    entity.governmental_non_profits,
    entity.fee_assessing_entity,
    entity.fs_reports_with_parent,
  ]);

  // Fiscal year display string
  const fiscalYearDisplay = useMemo(() => {
    const formatMonthDay = (iso?: string | null): string | null => {
      if (!iso) return null;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      d.setDate(d.getDate() + 1);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const begin = formatMonthDay(entity.fiscal_year_begins);
    const end = formatMonthDay(entity.fiscal_year_end);
    if (!begin && !end) return null;
    return `${begin || '—'} — ${end || '—'}`;
  }, [entity.fiscal_year_begins, entity.fiscal_year_end]);

  // Entity status display
  const statusLabel = safeDisplay(entity.entity_status);

  // Entity Status
  const statusIsHealthy = statusLabel !== '—' &&
    ['active', 'current', 'good standing'].some((s) => statusLabel.toLowerCase().includes(s));

  const statusIsWarning = statusLabel !== '—' &&
    ['delinquent'].some((s) => statusLabel.toLowerCase().includes(s));

  const statusIsDanger = statusLabel !== '—' &&
    ['on hold', 'hold', 'suspended', 'inactive', 'dissolved'].some((s) => statusLabel.toLowerCase().includes(s));

  // Pre-compute status icon class
  const statusIconClass = statusIsHealthy
    ? 'tu-overview__kpiIcon tu-overview__kpiIcon--green'
    : statusIsWarning
      ? 'tu-overview__kpiIcon tu-overview__kpiIcon--yellow'
      : statusIsDanger
        ? 'tu-overview__kpiIcon tu-overview__kpiIcon--red'
        : 'tu-overview__kpiIcon';

  // Pre-compute status FontAwesome icon
  const statusFaIcon = statusIsHealthy
    ? 'fas fa-check-circle'
    : statusIsWarning
      ? 'fas fa-exclamation-circle'
      : statusIsDanger
        ? 'fas fa-exclamation-triangle'
        : 'fas fa-info-circle';

  // Pre-compute status value color
  const statusValueColor = statusIsHealthy
    ? '#38A169'
    : statusIsWarning
      ? '#d69e2e'
      : statusIsDanger
        ? '#E53E3E'
        : undefined;

  // Form of Government 
  const formOfGov = safeDisplay(entity.form_of_government);
  const governingBodySeats = safeDisplay(entity.number_of_governing_body_seats);

  const isCouncilType = formOfGov !== '—' &&
    formOfGov.toLowerCase().includes('council');

  const formOfGovValue = formOfGov;

  const formOfGovSublabel = isCouncilType && governingBodySeats !== '—'
    ? `${governingBodySeats} Seats`
    : null;

  // Classification icon class
  const classIconClass = classificationFlags > 0
    ? 'tu-overview__kpiIcon tu-overview__kpiIcon--indigo'
    : 'tu-overview__kpiIcon';

  // Population
  const latestPopulation = useMemo(() => {
    if (!entityPopulationData || entityPopulationData.length === 0) return null;

    const allUndefined = entityPopulationData.every(
      (item) => item.census_population === undefined
    );
    if (allUndefined) return null;

    // Deduplicate by census_year — keep first occurrence (matches population component)
    const uniqueByYear = entityPopulationData.filter(
      (value, index, self) =>
        index === self.findIndex((i) => i.census_year === value.census_year)
    );

    // Filter to entries with valid population numbers
    const validEntries = uniqueByYear.filter(
      (p) => p.census_year && p.census_population !== undefined && p.census_population > 0
    );

    if (validEntries.length === 0) return null;

    // Sort descending by census year and grab the most recent
    const sorted = [...validEntries].sort((a, b) => b.census_year - a.census_year);
    return sorted[0];
  }, [entityPopulationData]);


  // Commented out for now - this might not be necessary
  // useEffect(() => {
  //   console.log(`Entity details data: ${entityDetailsData}`);
  //   if (!entityDetailsData || entityDetailsData.length === 0) return;
  //   console.log(`Entity details data: ${entityDetailsData}`);

  //   const mapped: EntityDetailsInterface[] = entityDetailsData.map((d) => ({
  //     entity_id: d.entity_id ? d.entity_id : 'Missing',
  //     entity_name: d.entity_name ? d.entity_name : 'Missing',
  //     entity_government_type: String(d.entity_government_type),
  //     entity_government_class: String(d.entity_government_class),
  //     entity_name_snakecase: String(d.entity_name_snakecase),
  //     sf_name: String(d.sf_name),
  //     sf_id: String(d.sf_id),
  //     tax_commission_id: String(d.tax_commission_id),
  //     ein: String(d.ein),
  //     finet_vendor_ids: String(d.finet_vendor_ids),
  //     transparency_id: String(d.transparency_id),

  //     sf_reports_to_transparent_utah: Boolean(d.sf_reports_to_transparent_utah),
  //     sf_reports_to_privacy: Boolean(d.sf_reports_to_privacy),
  //     sf_reports_to_local_gov: Boolean(d.sf_reports_to_local_gov),
  //     sf_reports_to_treasurer: Boolean(d.sf_reports_to_treasurer),

  //     public_infrastructure_district: Boolean(d.public_infrastructure_district),
  //     infrastructure_financing_district: Boolean(d.infrastructure_financing_district),
  //     special_project_entity: Boolean(d.special_project_entity),
  //     governmental_non_profits: Boolean(d.governmental_non_profits),
  //     fs_reports_with_parent: Boolean(d.fs_reports_with_parent),
  //     fee_assessing_entity: Boolean(d.fee_assessing_entity),
  //     component_unit_type: Boolean(d.component_unit_type),
  //     tax_holding_county: Boolean(d.tax_holding_county),

  //     created_in_salesforce_date: d.created_in_salesforce_date ? d.created_in_salesforce_date : 'Missing',
  //     creation_date: d.creation_date ? d.creation_date : 'Missing',

  //     website: d.website ? d.website : 'Missing',
  //     entity_description: d.entity_description ? d.entity_description : 'Missing',
  //     entity_purpose: d.entity_purpose ? d.entity_purpose : 'Missing',

  //     form_of_government: d.form_of_government ? d.form_of_government : 'Missing',
  //     number_of_governing_body_seats: d.number_of_governing_body_seats ? d.number_of_governing_body_seats : 'Missing',
  //     external_auditors: d.external_auditors ? d.external_auditors : 'Missing',

  //     billing_address: d.billing_address ? d.billing_address : 'Missing',
  //     billing_street: d.billing_street ? d.billing_street : 'Missing',
  //     billing_city: d.billing_city ? d.billing_city : 'Missing',
  //     billing_country: d.billing_country ? d.billing_country : 'Missing',
  //     billing_postal_code: d.billing_postal_code ? d.billing_postal_code : 'Missing',
  //     billing_state: d.billing_state ? d.billing_state : 'Missing',

  //     shipping_address: d.shipping_address ? d.shipping_address : 'Missing',
  //     shipping_street: d.shipping_street ? d.shipping_street : 'Missing',
  //     shipping_city: d.shipping_city ? d.shipping_city : 'Missing',
  //     shipping_postal_code: d.shipping_postal_code ? d.shipping_postal_code : 'Missing',
  //     shipping_state: d.shipping_state ? d.shipping_state : 'Missing',
  //     shipping_country: d.shipping_country ? d.shipping_country : 'Missing',

  //     fiscal_year_begins: d.fiscal_year_begins ? d.fiscal_year_begins : 'Missing',
  //     fiscal_year_end: d.fiscal_year_end ? d.fiscal_year_end : 'Missing',
  //     fiscal_year_change_effective_date: d.fiscal_year_change_effective_date ? d.fiscal_year_change_effective_date : 'Missing',
  //     fiscal_year_has_changed: d.fiscal_year_has_changed ? d.fiscal_year_has_changed : 'Missing',
  //     // transparency_report_expense_revenue_start_date: d.transparency_report_expense_revenue_start_date
  //     //   ? d.transparency_report_expense_revenue_start_date
  //     //   : 'Missing',
  //     transparency_report_expense_revenue_start_date: d.transparency_report_expense_revenue_start_date
  //       ? d.transparency_report_expense_revenue_start_date
  //       : 'Current', // Changed from missing to current
  //     // transparency_report_expense_revenue_end_date: d.transparency_report_expense_revenue_end_date
  //     //   ? d.transparency_report_expense_revenue_end_date
  //     //   : 'Present',
  //     transparency_report_expense_revenue_end_date: d.transparency_report_expense_revenue_end_date
  //       ? d.transparency_report_expense_revenue_end_date
  //       : 'Current', // Changed to current
  //     // transparency_report_wage_start_date: d.transparency_report_wage_start_date ? d.transparency_report_wage_start_date : 'Missing',
  //     transparency_report_wage_start_date: d.transparency_report_wage_start_date ? d.transparency_report_wage_start_date : 'Current', // Changed to current
  //     // transparency_report_wage_end_date: d.transparency_report_wage_end_date ? d.transparency_report_wage_end_date : 'Present',
  //     transparency_report_wage_end_date: d.transparency_report_wage_end_date ? d.transparency_report_wage_end_date : 'Current', // Changed to current

  //     entity_status: d.entity_status ? d.entity_status : 'Missing',
  //     hold_funds: Boolean(d.hold_funds),
  //     hold_deposit_and_investment: Boolean(d.hold_deposit_and_investment),
  //     suspend_funds: Boolean(d.suspend_funds),
  //     suspend_funds_deposit_and_investment: Boolean(d.suspend_funds_deposit_and_investment),
  //     freeze_funds: Boolean(d.freeze_funds),
  //     freeze_funds_deposit_and_investment: Boolean(d.freeze_funds_deposit_and_investment),
  //     transparency_delinquency: Boolean(d.transparency_delinquency),
  //     transparency_hold: Boolean(d.transparency_hold),
  //     transparency_suspend: Boolean(d.transparency_suspend),
  //   }));

  //   setEntityDetailsDataClean(mapped);
  // }, [entityDetailsData]);

  return (
    <Row className="mb-2">
      <Col xs={12}>
        {/* Keeping the original Card wrapper, but switching to brand header styling */}
        <Card bg="secondary" text="white" style={{ border: 'none', borderRadius: '0' }}>
          <Card.Body style={{ padding: 0 }}>
            <div className="tu-tabHeader">
              <div className="tu-tabHeader__titleRow">
                <h1 className="tu-tabHeader__title">Overview</h1>

                <button
                  type="button"
                  className="tu-tabHeader__infoBtn"
                  onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                  aria-label="Overview info"
                >
                  <i className="fas fa-info" />
                </button>
              </div>

              <Collapse in={showHeaderInfo}>
                <div className="tu-tabHeader__desc">
                  This page provides an overview of an entity&apos;s demographics, reporting, and history.
                </div>
              </Collapse>
            </div>
          </Card.Body>
        </Card>

        <Row className="g-0">
          {entityDetailsData && entityDetailsData.length > 0 ? (
            <div className="tu-entityOverview">

              {/* KPI Cards */}

              {/* Row 1 Cards */}
              <div className="tu-overview__kpiGrid tu-overview__kpiGrid--3col">

                {/* Government Type */}
                <div className="tu-overview__kpi">
                  <div className="tu-overview__kpiIcon">
                    <i className="fas fa-landmark" />
                  </div>
                  <div>
                    <div className="tu-overview__kpiValue">
                      {safeDisplay(entity.entity_government_type)}
                    </div>
                    <div className="tu-overview__kpiLabel">Government Type</div>
                  </div>
                </div>

                {/* Entity Status — Green: Active/Current, Yellow: Delinquent, Red: On Hold/Suspended */}
                <div className="tu-overview__kpi">
                  <div className={statusIconClass}>
                    <i className={statusFaIcon} />
                  </div>
                  <div>
                    <div className="tu-overview__kpiValue" style={{ color: statusValueColor }}>
                      {statusLabel}
                    </div>
                    <div className="tu-overview__kpiLabel">Entity Status</div>
                  </div>
                </div>

                {/* Established — shows age in years with "Years" suffix */}
                <div className="tu-overview__kpi">
                  <div className="tu-overview__kpiIcon">
                    <i className="fas fa-history" />
                  </div>
                  <div>
                    <div className="tu-overview__kpiValue">
                      {entityAge !== null ? `${entityAge} Years` : '—'}
                    </div>
                    <div className="tu-overview__kpiLabel">
                      {entity.creation_date ? `Est. ${formatDate(entity.creation_date)}` : 'Years Active'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 2: Operational Details */}
              <div className="tu-overview__kpiGrid">

                {/* Fiscal Year */}
                <div className="tu-overview__kpi">
                  <div className="tu-overview__kpiIcon">
                    <i className="fas fa-calendar-alt" />
                  </div>
                  <div>
                    <div className="tu-overview__kpiValue">
                      {fiscalYearDisplay || '—'}
                    </div>
                    <div className="tu-overview__kpiLabel">Fiscal Year Cycle</div>
                  </div>
                </div>

                {/* Form of Government — shows council type + seat count when applicable */}
                <div className="tu-overview__kpi">
                  <div className="tu-overview__kpiIcon">
                    <i className="fas fa-users" />
                  </div>
                  <div>
                    <div className="tu-overview__kpiValue">
                      {formOfGovValue}
                    </div>
                    <div className="tu-overview__kpiLabel">
                      {formOfGovSublabel
                        ? `Form of Government · ${formOfGovSublabel}`
                        : 'Form of Government'}
                    </div>
                  </div>
                </div>

                {/* Special Classifications */}
                <div className="tu-overview__kpi">
                  <div className={classIconClass}>
                    <i className="fas fa-tags" />
                  </div>
                  <div>
                    <div className="tu-overview__kpiValue">
                      {classificationFlags > 0 ? classificationFlags : 'None'}
                    </div>
                    <div className="tu-overview__kpiLabel">
                      {classificationFlags > 0
                        ? `Special Classification${classificationFlags !== 1 ? 's' : ''}`
                        : 'Special Classifications'}
                    </div>
                  </div>
                </div>

                {/* Population — latest census year, deduplicated to match population component */}
                <div className="tu-overview__kpi">
                  <div className="tu-overview__kpiIcon tu-overview__kpiIcon--indigo">
                    <i className="fas fa-users-cog" />
                  </div>
                  <div>
                    <div className="tu-overview__kpiValue">
                      {latestPopulation
                        ? latestPopulation.census_population.toLocaleString()
                        : '—'}
                    </div>
                    <div className="tu-overview__kpiLabel">
                      {latestPopulation
                        ? `Population (${latestPopulation.census_year})`
                        : 'Population'}
                    </div>
                  </div>
                </div>
              </div>


              {/* Main Grid */}

              {/* 1. General Information */}
              <SectionCard title="General Information" icon="fas fa-building">
                <DetailItem label="Fiscal Year Begins" value={stripFiscalYear(formatDate(entity.fiscal_year_begins))} />
                <DetailItem label="Fiscal Year Ends" value={stripFiscalYear(formatDate(entity.fiscal_year_end))} />
                <DetailItem label="Creation Date" value={formatDate(entity.creation_date)} />
                <DetailItem label="Form of Government" value={entity.form_of_government} />
                <DetailItem label="Number of Governing Body Seats" value={entity.number_of_governing_body_seats} />
                <DetailItem label="Date Added to State Reporting System" value={formatDate(entity.created_in_salesforce_date)} />

                <DetailItem
                  label="Entity Description (Beta)"
                  fullWidth
                  value={renderDescription(entity.entity_description)}
                />

                <DetailItem label="Entity Purpose (Beta)" value={entity.entity_purpose} fullWidth />

                {/* Addresses kept inside the same section */}
                <div className="tu-detail tu-detail--full">
                  <div className="tu-subsectionTitle">
                    <i className="fas fa-map-marker-alt" style={{ marginRight: 8 }} aria-hidden="true" />
                    Billing Address
                  </div>
                  <p className="tu-address">
                    {formatAddress(
                      entity.billing_street,
                      entity.billing_city,
                      entity.billing_state,
                      entity.billing_postal_code,
                      entity.billing_country
                    ) || 'No billing address on file.'}
                  </p>

                  <div className="tu-subsectionTitle">
                    <i className="fas fa-truck" style={{ marginRight: 8 }} aria-hidden="true" />
                    Shipping Address
                  </div>
                  <p className="tu-address">
                    {formatAddress(
                      entity.shipping_street,
                      entity.shipping_city,
                      entity.shipping_state,
                      entity.shipping_postal_code,
                      entity.shipping_country
                    ) || 'No shipping address on file.'}
                  </p>
                </div>
              </SectionCard>

              {/* 2. Compliance, Status & Holds (High Priority) */}
              <div className="tu-alertCard">
                <div className="tu-alertCard__header">
                  <div className="tu-alertHeaderRow">
                    <span className="tu-alertIcon" aria-hidden="true">
                      <i className="fas fa-clipboard-check" />
                    </span>
                    <h3 className="tu-alertCard__title">Reporting Obligations</h3>
                  </div>
                </div>

                <div className="tu-alertCard__body">
                  <div className="tu-kvRow">
                    <span>
                      <i className="fas fa-balance-scale" style={{ marginRight: 8 }} aria-hidden="true" />
                      State Auditor:
                    </span>
                    {formatBoolean(entity.sf_reports_to_local_gov)}
                  </div>

                  <div className="tu-kvRow">
                    <span>
                      <i className="fas fa-landmark" style={{ marginRight: 8 }} aria-hidden="true" />
                      State Treasurer:
                    </span>
                    {formatBoolean(entity.sf_reports_to_treasurer)}
                  </div>

                  <div className="tu-kvRow">
                    <span>
                      <i className="fas fa-eye" style={{ marginRight: 8 }} aria-hidden="true" />
                      Transparency Reporting:
                    </span>
                    {formatBoolean(entity.sf_reports_to_transparent_utah)}
                  </div>

                  <div className="tu-timespan">
                    <h5 className="tu-timespan__title">
                      <i className="far fa-calendar-alt" style={{ marginRight: 8 }} aria-hidden="true" />
                      Transparency Reporting Timespan
                    </h5>
                    <div className="tu-timespanGrid">
                      <div>
                        <div className="tu-timespanLabel">Quarterly Expense/Revenue</div>
                        <div className="tu-timespanValue">
                          {formatDate(entity.transparency_report_expense_revenue_start_date)} —{' '}
                          {formatDate(entity.transparency_report_expense_revenue_end_date)}
                        </div>
                      </div>

                      <div>
                        <div className="tu-timespanLabel">Annual Compensation</div>
                        <div className="tu-timespanValue">
                          {formatDate(entity.transparency_report_wage_start_date)} —{' '}
                          {formatDate(entity.transparency_report_wage_end_date)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 5. Entity Classifications (Flags) */}
              <SectionCard title="Classifications & Characteristics" icon="fas fa-tags">
                <DetailItem
                  label="Reports Financial Statements with Parent entity?"
                  value={formatBoolean(entity.fs_reports_with_parent)}
                />
                <DetailItem
                  label="Public Infrastructure District?"
                  value={formatBoolean(entity.public_infrastructure_district)}
                />
                <DetailItem
                  label="Infrastructure Financing District?"
                  value={formatBoolean(entity.infrastructure_financing_district)}
                />
                {/* Removed for now until this functionality is fully ready to be implemented */}
                {/* <DetailItem label="Special Project Entity?" value={formatBoolean(entity.special_project_entity)} /> */}
                <DetailItem label="Governmental Non-Profit?" value={formatBoolean(entity.governmental_non_profits)} />
                <DetailItem label="Fee Assessing Entity?" value={formatBoolean(entity.fee_assessing_entity)} />
                {/* <DetailItem label="Component Unit Type" value={formatBoolean(entity.component_unit_type)} /> */}
                <DetailItem label="Tax Holding County:" value={'Coming Soon'} />
                {/* <DetailItem label="Tax Holding County" value={entity.tax_holding_county} /> */}
              </SectionCard>

              {/* Moved lower down - 3. System Identifiers */}
              <SectionCard title="Known Identifiers" icon="fas fa-id-card">
                {/* <DetailItem label="Salesforce ID" value={entity.sf_id} /> */}
                {/* <DetailItem label="Salesforce Name" value={entity.sf_name} /> */}
                {/* <DetailItem label="Snakecase Name" value={entity.entity_name_snakecase} /> */}
                <DetailItem label="Tax Commission ID" value={entity.tax_commission_id} />
                <DetailItem label="EIN" value={entity.ein} />
                <DetailItem label="Transparency Database ID" value={entity.transparency_id} />
                {/* <DetailItem label="StateDataWarehouse/FINET Vendor IDs" value={entity.finet_vendor_ids} /> */}
              </SectionCard>
            </div>
          ) : (
            <Card className="mb-4" style={{}}>
              <div className="p-5 text-center">
                <div className="alert alert-light shadow-sm">
                  No data available
                </div>
              </div>
            </Card>
          )}
        </Row>
      </Col>
    </Row>
  );
};

export default EntityDetailsOverview;
